import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Grid,
  TextInput,
  Select,
  NumberInput,
  Textarea,
  Checkbox,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import { IconTrash, IconPencil, IconSettings, IconAlertTriangle } from "@tabler/icons";
import {
  DATA_AR_INVESTMENT_OBJECTIVE,
  DATA_AR_INVESTMENT_HORIZON,
  DATA_RPQ_RISK_LEVEL,
} from "../data/options";
import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "./pageList";
import LocalPageList from "./localPageList";
import ActionCellRender from "./actionCellRender";
import OrderArFormBlock, { OrderArFormStack } from "./orderArFormBlock";
import OrderArFormApprovedFundSelect from "./orderArFormApprovedFundSelect";
import OrderArFormProductDetails from "./orderArFormProductDetails";
import OrderArFormSubscribedAmount from "./orderArFormSubscribedAmount";
import OrderArFormProductConfirm from "./orderArFormProductConfirm";
import FormConfirmation from "./formConfirmation";
import { ApprovedFundListProvider } from "../contexts/approvedFundListContext";
import {
  AR_FORM_NUMBER_INPUT_PROPS,
  getArFormValues,
  isSuitabilityFullyAligned,
  hasComplexProductInArForm,
  setArFormField,
  setArFormNestedField,
  calcConcentrationRiskLevel,
  normalizeAlternativesConsideredList,
  EMPTY_AR_FORM_PRODUCT,
  validateSubscribedAmount,
} from "../helpers/arFormUtils";
import {
  getDerivativesFundPurchaseError,
  resolveDerivativesEligibilityClient,
} from "../helpers/derivativesFundEligibility";
import {
  getRpqFundPurchaseError,
  normalizeLatestRpqLevel,
  resolveArFormRpqFundContext,
  resolveClientRpqFundContext,
} from "../helpers/rpqFundEligibility";
import { formatFundAmount } from "../helpers/approvalFundListFundFields";
import {
  convertAmountToHkd,
  summarizeRecommendedProducts,
} from "../helpers/arFormInvestmentAmountUtils";
import { useInternalExchangeRates } from "../hooks/useInternalExchangeRates";
import {
  resolveDeclaredNetWealthFromClientAssets,
  shouldPrefillDeclaredNetWealth,
} from "../helpers/clientCurrentAssetsUtils";

const MAX_PRODUCTS = 8;
const MAX_ALTERNATIVES = 2;

const CONCENTRATION_LABEL_HEIGHT = 84;

const concentrationFieldStyles = {
  label: {
    minHeight: CONCENTRATION_LABEL_HEIGHT,
    lineHeight: 1.35,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    whiteSpace: "normal",
  },
  input: {
    height: 36,
    minHeight: 36,
  },
};

const BilingualFieldLabel = ({ english, chinese }) => (
  <>
    {english}
    <br />
    {chinese}
  </>
);

const RecommendedProductSubscribedAmountCell = ({ data, rateMap }) => {
  if (data.subscribedAmount == null) return "—";

  const amount = formatFundAmount(data.subscribedAmount);
  const currencyLabel = data.fundCurrency
    ? `${amount} ${data.fundCurrency}`
    : amount;
  const { hkdAmount, missingRate } = convertAmountToHkd(
    data.subscribedAmount,
    data.fundCurrency,
    rateMap
  );

  return (
    <Stack spacing={2}>
      <Text size="sm">{currencyLabel}</Text>
      {missingRate ? (
        <Text size="xs" color="orange">
          No internal rate for {data.fundCurrency}
        </Text>
      ) : hkdAmount != null ? (
        <Text size="xs" color="dimmed">
          ≈ {formatFundAmount(hkdAmount)} HKD
        </Text>
      ) : null}
    </Stack>
  );
};

const syncInvestmentAmount = (form, products, rateMap) => {
  const { totalHkd } = summarizeRecommendedProducts(products, rateMap);
  const nextAmount = totalHkd > 0 ? totalHkd : undefined;
  if (nextAmount == null) return;
  const current = getArFormValues(form).investmentAmount;
  if (current != null && current !== "" && Number(current) === nextAmount) return;
  setArFormField(form, "investmentAmount", nextAmount);
};

const RecommendedProductFundNameCell = ({ data }) => {
  const contractNo = String(data?.contractNo ?? "").trim();

  return (
    <Stack spacing={2}>
      <Text size="sm">{data?.fundName || "—"}</Text>
      {contractNo ? (
        <Text size="xs" color="green">
          {contractNo}
        </Text>
      ) : null}
    </Stack>
  );
};

const buildProductTableSchema = ({ rateMap = {}, allowDelete = true } = {}) => ({
  hidePagination: true,
  columns: [
    {
      field: "fundName",
      headerName: "Fund Name",
      cellRender: (col, data) => <RecommendedProductFundNameCell data={data} />,
    },
    {
      field: "subscribedAmount",
      headerName: "Subscribed/ Switch-in Amount",
      cellRender: (col, data) => (
        <RecommendedProductSubscribedAmountCell data={data} rateMap={rateMap} />
      ),
    },
    { field: "riskRating", headerName: "Risk (1-5)" },
    {
      field: "trailerFee",
      headerName: "Trailer Fee %",
      cellRender: (col, data) =>
        data.trailerFee != null ? `${data.trailerFee}%` : "—",
    },
    {
      field: "complexProduct",
      headerName: "Complex",
      cellRender: (col, data) => (data.complexProduct ? "Yes" : "No"),
    },
    {
      field: "derivativesFund",
      headerName: "Derivatives",
      cellRender: (col, data) => (data.derivativesFund ? "Yes" : "No"),
    },
    {
      field: "affiliatedFwd",
      headerName: "Affiliated #2",
      cellRender: (col, data) => (data.affiliatedFwd ? "Yes" : "No"),
    },
    {
      cellRender: ActionCellRender,
      isAction: true,
      actions: [
        { name: "edit", label: "Edit", icon: IconPencil, props: {} },
        ...(allowDelete
          ? [{ name: "delete", label: "Delete", icon: IconTrash, props: {} }]
          : []),
      ],
      actionMenu: [],
    },
  ],
});

const buildRecommendedProductFormSchema = (
  eligibilityClient = {},
  rpqContext = { riskLevel: null, status: "NONE", restrictFunds: false }
) => ({
  title: "Recommended Product",
  display: { mode: ENUM_FORM_DISPLAY.MODAL, size: "1000px" },
  showSaveBar: false,
  initialValues: {
    ...EMPTY_AR_FORM_PRODUCT(),
    _approvedFundKey: null,
  },
  validate: {
    fundName: (value, values) => {
      if (!String(value ?? "").trim()) return "Please select a fund";
      const derivativesError = getDerivativesFundPurchaseError(
        eligibilityClient,
        values
      );
      if (derivativesError) return derivativesError;
      return null;
    },
    riskRating: (value, values) => {
      if (values?._approvedFundKey) return null;
      return getRpqFundPurchaseError(rpqContext, { ...values, riskRating: value });
    },
    subscribedAmount: (value, values) => validateSubscribedAmount(value, values),
  },
  layout: {
    containers: [
      {
        key: "t1",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.WIZARD,
        props: {
          variant: "default",
          defaultValue: "Select Product",
          orientation: "vertical",
          style: { minHeight: "520px" },
          styles: {
            root: {
              display: "flex",
              gap: 24,
            },
            tabsList: {
              width: 220,
              minWidth: 220,
              flexShrink: 0,
              borderRight: "1px solid rgba(255, 255, 255, 0.08)",
              paddingRight: 16,
            },
            tab: {
              padding: "12px 16px",
              fontSize: 13,
            },
            panel: {
              flex: 1,
              minWidth: 0,
            },
          },
        },
        steps: [
          { key: "t1-1", label: "Select Product", icon: IconSettings },
          { key: "t1-2", label: "Subscribed/ Switch-in Amount", icon: IconSettings },
          { key: "t1-3", label: "Confirm", icon: IconSettings },
        ],
      },
      {
        key: "section-select",
        parent: "t1-1",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Select Product",
          description:
            "Choose a fund from the approved fund list 從認可基金列表中選擇基金",
          withBorder: false,
          withPaper: false,
          mt: 0,
          mb: 0,
          style: { minHeight: 0 },
        },
      },
      {
        key: "section-amount",
        parent: "t1-2",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Subscribed/ Switch-in Amount",
          description:
            "Enter the subscribed or switch-in amount in fund currency 輸入基金貨幣的認購/轉入金額",
          withBorder: false,
          withPaper: false,
          mt: 0,
          mb: 0,
          style: { minHeight: 0 },
        },
      },
      {
        key: "section-confirm",
        parent: "t1-3",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Confirm",
          description:
            "Review the details below and click Confirm to save 請確認以下資料",
          withBorder: false,
          withPaper: false,
          mt: 0,
          mb: 0,
          style: { minHeight: 0 },
        },
      },
    ],
    fields: [
      {
        name: "_approvedFundKey",
        component: OrderArFormApprovedFundSelect,
        parent: "section-select",
        props: {
          mb: "sm",
          eligibilityClient,
          rpqContext,
        },
      },
      {
        name: "_productDetails",
        component: OrderArFormProductDetails,
        parent: "section-select",
        props: {},
      },
      {
        name: "_subscribedAmount",
        component: OrderArFormSubscribedAmount,
        parent: "section-amount",
        props: {},
      },
      {
        name: "_productConfirm",
        component: OrderArFormProductConfirm,
        parent: "section-confirm",
        props: {},
      },
      {
        name: "_formConfirmation",
        component: FormConfirmation,
        parent: "section-confirm",
        props: {
          validateOnMount: true,
          successText: [
            "Please review the product details above and click Confirm to save.",
          ],
        },
      },
    ],
  },
});

const buildAlternativeProductFormSchema = (
  rpqContext = { riskLevel: null, status: "NONE", restrictFunds: false }
) => ({
  title: "Alternative Product",
  display: { mode: ENUM_FORM_DISPLAY.MODAL, size: "xl" },
  initialValues: {
    ...EMPTY_AR_FORM_PRODUCT(),
    _approvedFundKey: null,
  },
  validate: {
    fundName: (value) =>
      String(value ?? "").trim() ? null : "Please select a fund",
  },
  layout: {
    containers: [
      {
        key: "product-subscribed",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Product subscribed 產品認購",
          withBorder: false,
          withPaper: false,
          minHeight: 0,
        },
      },
    ],
    fields: [
      {
        name: "_approvedFundKey",
        component: OrderArFormApprovedFundSelect,
        parent: "product-subscribed",
        props: {
          mb: "sm",
          rpqContext,
        },
      },
      {
        name: "_productDetails",
        component: OrderArFormProductDetails,
        parent: "product-subscribed",
        props: {},
      },
    ],
  },
});

const stripInternalProductFields = (item = {}) => {
  const {
    _approvedFundKey,
    _productDetails,
    _productSubscribeWizard,
    _subscribedAmount,
    _productConfirm,
    _formConfirmation,
    ...rest
  } = item;
  return rest;
};

const OrderArFormPartB = ({ form }) => {
  const arForm = getArFormValues(form);
  const { rateMap } = useInternalExchangeRates();
  const productSummary = useMemo(
    () => summarizeRecommendedProducts(arForm.recommendedProducts ?? [], rateMap),
    [arForm.recommendedProducts, rateMap]
  );
  const suitability = arForm.suitability ?? {};
  const allSuitabilityAligned = isSuitabilityFullyAligned(suitability);
  const hasComplexProducts = useMemo(
    () => hasComplexProductInArForm(arForm),
    [arForm.recommendedProducts, arForm.alternativesConsidered]
  );
  const [rows, setRows] = useState(arForm.recommendedProducts ?? []);
  const [alternativeRows, setAlternativeRows] = useState(
    normalizeAlternativesConsideredList(arForm.alternativesConsidered)
  );

  useEffect(() => {
    setRows(arForm.recommendedProducts ?? []);
    setAlternativeRows(
      normalizeAlternativesConsideredList(arForm.alternativesConsidered)
    );
  }, [form.values?._id]);

  useEffect(() => {
    const nextAmount =
      productSummary.totalHkd > 0 ? productSummary.totalHkd : undefined;
    if (nextAmount == null) return;
    const current = getArFormValues(form).investmentAmount;
    if (current != null && current !== "" && Number(current) === nextAmount) return;
    setArFormField(form, "investmentAmount", nextAmount);
    // Recompute only when the product HKD total changes, so a typed amount
    // stays until the recommended products (or their FX) change.
  }, [productSummary.totalHkd]);

  useEffect(() => {
    if (!shouldPrefillDeclaredNetWealth(arForm.declaredNetWealth)) return;

    const client = form.values?.orderId?.client;
    const prefillValue = resolveDeclaredNetWealthFromClientAssets(client, rateMap);
    if (prefillValue == null || arForm.declaredNetWealth === prefillValue) return;

    setArFormField(form, "declaredNetWealth", prefillValue);
  }, [
    arForm.declaredNetWealth,
    form,
    form.values?.orderId?.client,
    rateMap,
  ]);

  useEffect(() => {
    if (normalizeLatestRpqLevel(arForm.latestRpqLevel) != null) return;
    const clientLevel = normalizeLatestRpqLevel(
      resolveClientRpqFundContext(form.values).riskLevel
    );
    if (clientLevel == null) return;
    setArFormField(form, "latestRpqLevel", clientLevel);
  }, [
    arForm.latestRpqLevel,
    form,
    form.values?.orderId?.client?.rpq,
    form.values?.orderId?.rpq,
    form.values?.rpq,
    form.values?.orderId?.client?.rpqStatus,
    form.values?.orderId?.rpqStatus,
    form.values?.rpqStatus,
  ]);

  useEffect(() => {
    const level = calcConcentrationRiskLevel(
      arForm.investmentAmount,
      arForm.declaredNetWealth
    );
    if (level != null && arForm.concentrationRiskLevel !== level) {
      setArFormField(form, "concentrationRiskLevel", level);
    }
  }, [arForm.investmentAmount, arForm.declaredNetWealth]);

  useEffect(() => {
    if (!allSuitabilityAligned) return;
    if (!(arForm.suitability?.mismatchRemark ?? "").trim()) return;
    setArFormNestedField(form, "suitability.mismatchRemark", "");
    form.clearFieldError?.("arForm.suitability.mismatchRemark");
  }, [allSuitabilityAligned, arForm.suitability?.mismatchRemark, form]);

  useEffect(() => {
    if (hasComplexProducts) {
      form.validateField?.("arForm.complexProductConcentration");
      return;
    }
    if (arForm.complexProductConcentration != null) {
      setArFormField(form, "complexProductConcentration", undefined);
    }
    form.clearFieldError?.("arForm.complexProductConcentration");
  }, [hasComplexProducts, arForm.complexProductConcentration, form]);

  const eligibilityClient = useMemo(
    () => resolveDerivativesEligibilityClient(form.values),
    [
      form.values?.orderId?.professionalInvestor,
      form.values?.orderId?.client?.professionalInvestor,
      form.values?.orderId?.client?.knowledgeOfDerivatives,
    ]
  );
  const rpqContext = useMemo(
    () => resolveArFormRpqFundContext(form.values),
    [
      arForm.latestRpqLevel,
      form.values?.orderId?.client?.rpq,
      form.values?.orderId?.rpq,
      form.values?.rpq,
      form.values?.orderId?.client?.rpqStatus,
      form.values?.orderId?.rpqStatus,
      form.values?.rpqStatus,
    ]
  );
  const tableSchema = useMemo(
    () => buildProductTableSchema({ rateMap }),
    [rateMap]
  );
  const formSchema = useMemo(
    () => buildRecommendedProductFormSchema(eligibilityClient, rpqContext),
    [eligibilityClient, rpqContext]
  );
  const alternativeTableSchema = buildProductTableSchema();
  const alternativeFormSchema = useMemo(
    () => buildAlternativeProductFormSchema(rpqContext),
    [rpqContext]
  );

  const onProductsSubmit = (values) => {
    const next = values.map(stripInternalProductFields);
    setRows(next);
    setArFormField(form, "recommendedProducts", next);
    syncInvestmentAmount(form, next, rateMap);
  };

  const onAlternativesSubmit = (values) => {
    const next = values
      .map((item) => {
        if (typeof item === "string") {
          return item.trim() ? { fundName: item.trim() } : null;
        }
        return stripInternalProductFields(item);
      })
      .filter(Boolean);
    setAlternativeRows(next);
    setArFormField(form, "alternativesConsidered", next);
  };

  return (
    <ApprovedFundListProvider>
      <OrderArFormStack>
      <OrderArFormBlock
        title="1. Client investment profile"
        subtitle="Objectives, horizon, and suitability (Part B §1a–1c)"
      >
        <Stack spacing="md">
          <div>
            <Checkbox.Group
              label="Investment objective 客戶投資目標"
              value={arForm.investmentObjective ?? []}
              onChange={(values) =>
                setArFormField(form, "investmentObjective", values)
              }
            >
              <Group mt="xs">
                {DATA_AR_INVESTMENT_OBJECTIVE.map((opt) => (
                  <Checkbox key={opt.value} value={opt.value} label={opt.label} />
                ))}
              </Group>
            </Checkbox.Group>
            {(arForm.investmentObjective ?? []).includes("Other") && (
              <TextInput
                label="Other objective 其他，請註明"
                mt="sm"
                value={arForm.investmentObjectiveOther ?? ""}
                onChange={(e) =>
                  setArFormField(
                    form,
                    "investmentObjectiveOther",
                    e.currentTarget.value
                  )
                }
              />
            )}
          </div>

          <Select
            label="Investment horizon 客戶預期投資年期"
            data={DATA_AR_INVESTMENT_HORIZON}
            value={arForm.investmentHorizon ?? null}
            onChange={(v) => setArFormField(form, "investmentHorizon", v)}
            searchable
            clearable
          />

          <Select
            label="Latest RPQ Level 最新 RPQ 風險級別"
            data={DATA_RPQ_RISK_LEVEL}
            value={
              arForm.latestRpqLevel == null ? null : arForm.latestRpqLevel
            }
            onChange={(v) => setArFormField(form, "latestRpqLevel", v)}
            clearable
          />

          <Stack spacing="xs">
            <Checkbox
              label="Aligned with risk tolerance (RPQ) 與風險評估問卷結果相符"
              checked={arForm.suitability?.riskTolerance ?? false}
              onChange={(e) =>
                setArFormNestedField(
                  form,
                  "suitability.riskTolerance",
                  e.currentTarget.checked
                )
              }
            />
            <Checkbox
              label="Aligned with investment objective 與投資目標相符"
              checked={arForm.suitability?.objective ?? false}
              onChange={(e) =>
                setArFormNestedField(
                  form,
                  "suitability.objective",
                  e.currentTarget.checked
                )
              }
            />
            <Checkbox
              label="Aligned with investment horizon 與預期投資年期相符"
              checked={arForm.suitability?.horizon ?? false}
              onChange={(e) =>
                setArFormNestedField(
                  form,
                  "suitability.horizon",
                  e.currentTarget.checked
                )
              }
            />
            {!allSuitabilityAligned && (
              <Textarea
                label="Mismatch details 與客戶投資預期不符的情況，請詳述"
                required
                error={form.errors?.["arForm.suitability.mismatchRemark"]}
                value={arForm.suitability?.mismatchRemark ?? ""}
                onChange={(e) =>
                  setArFormNestedField(
                    form,
                    "suitability.mismatchRemark",
                    e.currentTarget.value
                  )
                }
                minRows={2}
              />
            )}
          </Stack>
        </Stack>
      </OrderArFormBlock>

      <OrderArFormBlock
        title="Advisor analysis"
        subtitle="1(d) 理財顧問分析"
      >
        <Textarea
          label="Analysis 分析"
          value={arForm.advisorAnalysis ?? ""}
          onChange={(e) =>
            setArFormField(form, "advisorAnalysis", e.currentTarget.value)
          }
          minRows={3}
        />
      </OrderArFormBlock>

      <OrderArFormBlock
        title="2. Recommended products"
        subtitle="建議的特定產品 — up to 8 funds"
      >
        <Stack spacing="sm">
          <LocalPageList
            rows={rows}
            onFormSubmit={onProductsSubmit}
            tableSchema={tableSchema}
            formSchema={formSchema}
            btnLabel="Add Product"
            readOnly={rows.length >= MAX_PRODUCTS}
          />
          {productSummary.missingCurrencies.length > 0 && (
            <Alert
              icon={<IconAlertTriangle size={16} />}
              color="orange"
              title="Missing internal exchange rates 缺少內部匯率"
            >
              No internal rate found for:{" "}
              {productSummary.missingCurrencies.join(", ")}. Add rates in
              Management → Exchange Rate. Investment Amount excludes these
              products until rates are configured.
            </Alert>
          )}
        </Stack>
      </OrderArFormBlock>

      <OrderArFormBlock
        title="Alternatives considered"
        subtitle="其它選擇 — up to 2 funds"
      >
        <LocalPageList
          rows={alternativeRows}
          onFormSubmit={onAlternativesSubmit}
          tableSchema={alternativeTableSchema}
          formSchema={alternativeFormSchema}
          btnLabel="Add Alternative"
          readOnly={alternativeRows.length >= MAX_ALTERNATIVES}
        />
      </OrderArFormBlock>

      <OrderArFormBlock
        title="3. Concentration risk"
        subtitle="風險集中水平"
      >
        <Grid gutter="md" align="stretch">
          <Grid.Col span={12} md={4}>
            <NumberInput
              styles={concentrationFieldStyles}
              hideControls
              label={
                <BilingualFieldLabel
                  english="Declared net wealth (excl. property) HKD"
                  chinese="淨財富聲明（不包括物業）HKD"
                />
              }
              value={arForm.declaredNetWealth ?? ""}
              onChange={(v) => setArFormField(form, "declaredNetWealth", v)}
              precision={2}
              min={0}
              {...AR_FORM_NUMBER_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={12} md={4}>
            <NumberInput
              styles={concentrationFieldStyles}
              hideControls
              label={
                <BilingualFieldLabel
                  english="Concentration risk level % (auto)"
                  chinese="風險集中水平 %（自動）"
                />
              }
              value={arForm.concentrationRiskLevel ?? ""}
              readOnly
              precision={2}
              {...AR_FORM_NUMBER_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={12} md={4}>
            <NumberInput
              styles={concentrationFieldStyles}
              hideControls
              label={
                <BilingualFieldLabel
                  english="Complex product concentration %"
                  chinese="複雜產品風險集中水平"
                />
              }
              required={hasComplexProducts}
              withAsterisk={hasComplexProducts}
              error={form.errors?.["arForm.complexProductConcentration"]}
              value={arForm.complexProductConcentration ?? ""}
              onChange={(v) =>
                setArFormField(form, "complexProductConcentration", v)
              }
              precision={2}
              min={0}
              {...AR_FORM_NUMBER_INPUT_PROPS}
            />
          </Grid.Col>
        </Grid>
      </OrderArFormBlock>
      </OrderArFormStack>
    </ApprovedFundListProvider>
  );
};

export default OrderArFormPartB;
